import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { groupBy, isEqual, isEmpty } from 'lodash';
import { Button, Card, Modal } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { useHistory } from 'react-router-dom';

import { ActionId } from 'admin/actions/Actions';
import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { useProfile, isAuthorized } from 'auth/Auth';
import { SystemLabel } from 'common/comments2/Comments2.types';
import { DesktopAlertComponentProps } from 'common/notification-tray/desktop-alert-registry/DesktopAlertRegistry';
import { ReviewDesktopAlertPopup } from 'common/notification-tray/desktop-alert-types/review-desktop-alert/review-desktop-alert-popup/ReviewDesktopAlertPopup';
import { ProActActionModalBanner } from 'common/notification-tray/proact-action-modal/ProActActionModalBanner';
import { isVobEnabled } from 'common/response-editor/vob/VobUtils';
import { TemplateLevel } from 'common/templates/Templates';
import { getReviewsAIImpactAnalysis } from 'common/util/AIImpactAnalysis.api';
import { Overlay, OverlayMode } from 'core/overlay/Overlay';
import { Pager } from 'core/pager/Pager';
import { milliSecondsToDHMS } from 'core/util/DateRange';
import { usePrevious } from 'core/util/Hook';
import { Route } from 'core/util/Route';
import { Color } from 'core/util/Variables';
import { Handlers, ReviewMode } from 'reviews2/feed/review-yeti/Review.types';
import { ReviewYeti } from 'reviews2/feed/review-yeti/ReviewYeti';
import { Reviews3FieldName, RespondableType } from 'reviews2/feed/Reviews.types';
import { getReview3, getReviews3 } from 'reviews2/Reviews.api';

import 'common/notification-tray/desktop-alert-types/review-desktop-alert/ReviewDesktopAlert.scss';

const PROMISE_AREA = 'review-desktop-alert';

export const ReviewDesktopAlert: FunctionComponent<DesktopAlertComponentProps> = ({ alert, onClose }) => {
    const { objectIDs = [], metadata = {}, tenantID } = alert;
    const history = useHistory();
    const { t } = useTranslation('reviews2');
    const [canNextPage, setCanNextPage] = useState(false);
    const [canPreviousPage, setCanPreviousPage] = useState(false);
    const sortedObjectIds = useMemo(() => {
        // always show the review that the user selected first, if available
        if (metadata?.reviewId) {
            const sorted = [...objectIDs].sort((a, b) => {
                if (a === metadata?.reviewId) return -1;
                if (b === metadata?.reviewId) return 1;
                return 0;
            });

            return sorted;
        }

        return objectIDs;
    }, [metadata?.reviewId, objectIDs]);
    const [currentPageCount] = useState(objectIDs.length);
    const [currentPage, setCurrentPage] = useState(0);
    const previousPage = usePrevious(currentPage);
    const [currentReview, setCurrentReview] = useState<any>(null);
    const [{ profile }] = useProfile();
    const [respondedReviews, setRespondedReviews] = useState<any[]>([]);
    const [unrespondableReviews, setUnrespondableReviews] = useState<any[]>([]);
    const start = useMemo(() => Date.now(), []);
    const [fetchReviewsError, setFetchReviewsError] = useState<boolean>(false);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [popupContent, setPopupContent] = useState<JSX.Element | null>(null);
    const canApprove = useMemo(() => isAuthorized({ actions: [ActionId.ReviewResponses.Approve] }), []);
    const isCurrentReviewResponded = useMemo(
        () => respondedReviews.some((r) => r.document_id === currentReview?.document_id),
        [respondedReviews, currentReview],
    );
    const [impactAnalysisConfig, setImpactAnalysisConfig] = useState<Record<string, number>>({
        min: 0,
        max: 0,
        mina: 0,
        maxa: 0,
        count: 0,
    });
    const args = useMemo(() => {
        return {
            includePage: true,
            includeTenant: true,
            includeLabels: true,
            includeSource: true,
            includeLocation: true,
            includeResponses: true,
            includeCredentials: true,
            includeCredentialStatus: true,
            includeAttributionUsers: true,
            includeRecommendedReplies: true,
        };
    }, []);
    const handlers: Handlers = useMemo(() => {
        return {
            onSuccessfulResponse: ({ review }) => {
                const responded = respondedReviews.some((r) => r.document_id === review.document_id);

                if (!responded) {
                    setRespondedReviews((prev) => [...prev, review]);
                }
            },
            onSaveCredential: async () => {
                setCurrentReview(null);

                const { review: updatedReview } = await trackPromise(
                    getReview3(currentReview?.r4e_tenant_id, currentReview.document_id, args),
                    PROMISE_AREA,
                );

                setCurrentReview({ ...currentReview, ...updatedReview });
            },
        };
    }, [currentReview, respondedReviews, args]);
    const vobEnabled = isVobEnabled(profile?.tenant, profile);
    const canGenerateAIResponse =
        isAuthorized({
            actions: [ActionId.ReviewResponses.AIResponses],
        }) &&
        isAuthorized({
            featureFlags: [FeatureFlagId.AISuggestedResponse],
        }) &&
        !vobEnabled;

    const nextHandler = () => {
        setCurrentPage(currentPage + 1);
    };

    const previousHandler = () => {
        setCurrentPage(currentPage - 1);
    };

    const fetchReview = useCallback(
        async (reviewId: string) => {
            setFetchReviewsError(false);

            try {
                const { review, responses } = await trackPromise(
                    getReview3(Number(tenantID), reviewId, args),
                    PROMISE_AREA,
                );

                review.replies = responses;

                setCurrentReview(review);
            } catch (e) {
                setFetchReviewsError(true);
            }
        },
        [tenantID, args],
    );

    const toFeed = useCallback((): void => {
        history.push(Route.Reviews3);
    }, [history]);

    const handleIncomplete = useCallback((): void => {
        const isSingleReview = objectIDs.length === 1;
        const noResponses = !respondedReviews.length && unrespondableReviews.length !== objectIDs.length;
        let title;
        let message;

        if (noResponses) {
            title = isSingleReview ? t('INCOMPLETE_RESPONSE_TITLE') : t('INCOMPLETE_RESPONSE_TITLE_plural');
            message = isSingleReview ? t('INCOMPLETE_RESPONSE_MESSAGE') : t('INCOMPLETE_RESPONSE_MESSAGE_plural');
        } else {
            title = t('PARTIAL_RESPONSE_TITLE_plural');
            message = t('PARTIAL_RESPONSE_MESSAGE_plural');
        }

        setPopupContent(
            <ReviewDesktopAlertPopup
                icon={<i className="icon-exclamation-triangle" />}
                title={title}
                message={message}
                metrics={
                    <>
                        <div className="alert-metrics" style={{ background: Color.Orange7 }}>
                            <div className="alert-metric">
                                <div className="alert-metric-value" style={{ color: Color.Orange1 }}>
                                    {respondedReviews.length}/{objectIDs.length - unrespondableReviews.length}
                                </div>
                                <div className="alert-metric-label">{t('RESPONDED_REVIEWS')}</div>
                            </div>
                            {unrespondableReviews.length ? (
                                <div className="alert-metric">
                                    <div className="alert-metric-value" style={{ color: Color.Orange1 }}>
                                        {unrespondableReviews.length}
                                    </div>
                                    <div className="alert-metric-label">
                                        {t('UNRESPONDABLE_REVIEWS', { count: unrespondableReviews.length })}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </>
                }
                actions={
                    <>
                        <Button
                            variant="outline-danger"
                            onClick={() => {
                                setShowPopup(false);
                                onClose();
                            }}
                        >
                            {t('STOP_RESPONDING')}
                        </Button>{' '}
                        <Button variant="primary" onClick={() => setShowPopup(false)}>
                            {t('CONTINUE_RESPONDING')}
                        </Button>
                    </>
                }
            />,
        );
        setShowPopup(true);
    }, [objectIDs, respondedReviews, unrespondableReviews, t, onClose]);

    const handleComplete = useCallback((): void => {
        const time = Date.now() - start;

        setPopupContent(
            <ReviewDesktopAlertPopup
                icon={canApprove ? <i className="icon-check" /> : <i className="icon-hourglass" />}
                title={
                    respondedReviews.length === 1
                        ? canApprove
                            ? t('COMPLETE_RESPONSE_TITLE')
                            : t('COMPELTE_RESPONSE_TITLE_NO_APPROVE')
                        : canApprove
                        ? t('COMPLETE_RESPONSE_TITLE_plural')
                        : t('COMPELTE_RESPONSE_TITLE_NO_APPROVE_plural')
                }
                message={
                    respondedReviews.length === 1
                        ? canApprove
                            ? t('COMPLETE_RESPONSE_MESSAGE')
                            : t('COMPLETE_RESPONSE_MESSAGE_NO_APPROVE')
                        : canApprove
                        ? t('COMPLETE_RESPONSE_MESSAGE_plural')
                        : t('COMPLETE_RESPONSE_MESSAGE_NO_APPROVE_plural')
                }
                metrics={
                    <div
                        className="alert-metrics"
                        style={{ background: canApprove ? Color.TrueGreen4 : Color.Yellow4 }}
                    >
                        <div className="alert-metric">
                            <div
                                className="alert-metric-value"
                                style={{ color: canApprove ? Color.DarkGreen1 : Color.Yellow1 }}
                            >
                                {respondedReviews.length}
                            </div>
                            <div className="alert-metric-label">{t('RESPONDED_REVIEWS')}</div>
                        </div>
                        {unrespondableReviews.length ? (
                            <div className="alert-metric">
                                <div
                                    className="alert-metric-value"
                                    style={{ color: canApprove ? Color.DarkGreen1 : Color.Yellow1 }}
                                >
                                    {unrespondableReviews.length}
                                </div>
                                <div className="alert-metric-label">{t('UNRESPONDABLE_REVIEWS')}</div>
                            </div>
                        ) : null}
                        <div className="alert-metric">
                            <div
                                className="alert-metric-value"
                                style={{ color: canApprove ? Color.DarkGreen1 : Color.Yellow1 }}
                            >
                                {milliSecondsToDHMS(time)}
                            </div>
                            <div className="alert-metric-label">{t('TIME_TAKEN')}</div>
                        </div>
                    </div>
                }
                actions={
                    <>
                        <Button
                            variant="link"
                            onClick={() => {
                                setShowPopup(false);
                                onClose();
                            }}
                        >
                            {t('CLOSE_ALERT')}
                        </Button>{' '}
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowPopup(false);
                                onClose();
                                toFeed();
                            }}
                        >
                            {t('VIEW_ALL_REVIEW_RESPONSES')}
                        </Button>{' '}
                    </>
                }
            />,
        );
        setShowPopup(true);
    }, [start, respondedReviews, unrespondableReviews, toFeed, t, canApprove, onClose]);

    const handleAllReviewsUnrespondable = useCallback((): void => {
        setPopupContent(
            <ReviewDesktopAlertPopup
                icon={<i className="icon-check" />}
                title={
                    unrespondableReviews.length === 1
                        ? t('ALL_UNRESPONDABLE_TITLE')
                        : t('ALL_UNRESPONDABLE_TITLE_plural')
                }
                message={
                    unrespondableReviews.length === 1
                        ? t('ALL_UNRESPONDABLE_MESSAGE')
                        : t('ALL_UNRESPONDABLE_MESSAGE_plural')
                }
                metrics={
                    <div className="alert-metrics" style={{ background: Color.TrueGreen4 }}>
                        <div className="alert-metric">
                            <div className="alert-metric-value" style={{ color: Color.DarkGreen1 }}>
                                {unrespondableReviews.length}
                            </div>
                            <div className="alert-metric-label">{t('UNRESPONDABLE_REVIEWS')}</div>
                        </div>
                    </div>
                }
                actions={
                    <>
                        <Button
                            variant="link"
                            onClick={() => {
                                setShowPopup(false);
                                onClose();
                            }}
                        >
                            {t('CLOSE_ALERT')}
                        </Button>{' '}
                    </>
                }
            />,
        );
        setShowPopup(true);
    }, [t, unrespondableReviews, onClose]);

    const finish = useCallback(() => {
        const allReviewsUnrespondable = objectIDs.every((id) =>
            unrespondableReviews.some(({ review }) => review.document_id === id),
        );
        const isComplete = objectIDs.length - unrespondableReviews.length === respondedReviews.length;

        if (allReviewsUnrespondable) {
            handleAllReviewsUnrespondable();
            return;
        }

        if (isComplete) {
            handleComplete();
            return;
        }

        handleIncomplete();
    }, [
        objectIDs,
        unrespondableReviews,
        respondedReviews,
        handleAllReviewsUnrespondable,
        handleComplete,
        handleIncomplete,
    ]);

    const getImpactAnalysisText = (impactAnalysisConfig: Record<string, number>) => {
        const { mina, maxa, count } = impactAnalysisConfig;

        if (!count) {
            return <>{t('REVIEW_DESKTOP_ALERT_TEXT_NO_DATA')}</>;
        }

        return (
            <Trans i18nKey="REVIEW_DESKTOP_ALERT_TEXT" values={{ mina, maxa }}>
                Quick responses can reduce negative impact and improve your Reputation Score by{' '}
                <strong>
                    {{ mina }} to {{ maxa }}
                </strong>{' '}
                points.
            </Trans>
        );
    };

    useEffect(() => {
        if (!isEqual(currentPage, previousPage)) {
            setCurrentReview(null);
            setCanNextPage(currentPage + 1 < currentPageCount);
            setCanPreviousPage(currentPage > 0);

            const reviewId = sortedObjectIds[currentPage];

            if (reviewId) {
                fetchReview(reviewId);
            }
        }
    }, [fetchReview, sortedObjectIds, currentPage, currentPageCount, previousPage]);

    useEffect(() => {
        (async () => {
            const filter = {
                name: Reviews3FieldName.DocumentId,
                values: objectIDs,
            };
            const projections = [
                Reviews3FieldName.Id,
                Reviews3FieldName.Date,
                Reviews3FieldName.Source,
                Reviews3FieldName.LocationUid,
                Reviews3FieldName.RespondableType,
                Reviews3FieldName.NormalizedRating,
                Reviews3FieldName.Labels,
            ];
            const reviews = await getReviews3(Number(tenantID), { filters: [filter], projections });
            const reviewsByLocationUid = groupBy(reviews, `review.${Reviews3FieldName.LocationUid}`);
            const locationReviews = Object.entries(reviewsByLocationUid).map(([location_uid, reviews]) => {
                return {
                    location_uid: Number(location_uid),
                    review_details: reviews.map(({ review }) => {
                        return {
                            source_id: review.review_source_id,
                            timestamp: review.post_created_at,
                            normalized_rating: review.rep_normalized_rating,
                            responded: review.respondable_type === RespondableType.Responded,
                        };
                    }),
                };
            });
            const { location_impacts } = await getReviewsAIImpactAnalysis(Number(tenantID), {
                location_reviews: locationReviews,
            });
            const processedImpactConfig = location_impacts?.reduce(
                (acc, { no_data, min_impact = 0, max_impact = 0 }) => {
                    if (no_data === false) {
                        acc.max += max_impact;
                        acc.min += min_impact;
                        acc.count += 1;
                    }

                    return acc;
                },
                impactAnalysisConfig,
            );
            const unrespondableReviews = reviews?.filter(
                ({ review }) =>
                    review.respondable_type === RespondableType.NotRespondable ||
                    review.r4e_label_ids.includes(SystemLabel.NoResponseRequired),
            );

            if (processedImpactConfig.count > 0) {
                processedImpactConfig.mina = Math.round(processedImpactConfig.min / processedImpactConfig.count);
                processedImpactConfig.maxa = Math.round(processedImpactConfig.max / processedImpactConfig.count);
            }

            setUnrespondableReviews(unrespondableReviews);
            setImpactAnalysisConfig(processedImpactConfig);
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Modal className="review-desktop-alert" size="xl" show centered onHide={finish}>
                <Modal.Header closeButton>
                    <Modal.Title>{alert.payload}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProActActionModalBanner
                        title={t('REVIEW_DESKTOP_ALERT_TITLE')}
                        text={getImpactAnalysisText(impactAnalysisConfig)}
                    />
                    {currentReview ? (
                        <ReviewYeti
                            review={currentReview}
                            profile={profile}
                            handlers={handlers}
                            rrid={
                                currentReview.document_id === metadata?.reviewId && !isCurrentReviewResponded
                                    ? metadata?.responseId
                                    : undefined
                            }
                            mode={ReviewMode.Respond}
                            options={{
                                showReviewerName: isAuthorized({ actions: [ActionId.PlatformPII.ViewPublic] }),
                                editorOptions: {
                                    placeholder: `${t('REPLY_WRITE')}${
                                        canGenerateAIResponse ? t('OR_GENERATE_RESPONSE') : ''
                                    }`,
                                    enableTemplates: isAuthorized({
                                        actions: [
                                            ActionId.PlatformTemplates.View,
                                            ActionId.PlatformTemplates.ViewSystem,
                                            ActionId.PlatformTemplates.ViewTenant,
                                        ],
                                    }),
                                    templateLevels: [
                                        ...(isAuthorized({ actions: [ActionId.PlatformTemplates.ViewSystem] })
                                            ? [TemplateLevel.System]
                                            : []),
                                        ...(isAuthorized({ actions: [ActionId.PlatformTemplates.ViewTenant] })
                                            ? [TemplateLevel.Tenant]
                                            : []),
                                        ...(isAuthorized({ actions: [ActionId.PlatformTemplates.View] })
                                            ? [TemplateLevel.Location, TemplateLevel.User]
                                            : []),
                                    ],
                                    responseGeneratorOptions: {
                                        enable: canGenerateAIResponse,
                                        disableButton:
                                            !currentReview.raw_body_text?.length && !currentReview.properties?.length,
                                        disabledMessage: t('RESPONSE_GEN_REVIEW_NO_TEXT'),
                                    },
                                    respondButtonOptions: {
                                        enable: true,
                                    },
                                    toolBarOptions: {
                                        enable: true,
                                    },
                                    generateAIResponseOnLoad:
                                        vobEnabled &&
                                        !isCurrentReviewResponded &&
                                        (isEmpty(metadata?.responseId) ||
                                            !isEqual(currentReview.document_id, metadata?.reviewId)),
                                    defaultText:
                                        !isEmpty(metadata?.responseId) &&
                                        isEqual(currentReview.document_id, metadata?.reviewId)
                                            ? currentReview.recommendedReplies?.[0]?.text
                                            : undefined,
                                },
                            }}
                        />
                    ) : (
                        <Card>
                            <Overlay area={PROMISE_AREA} mode={OverlayMode.Skeleton} />
                            <Card.Body style={{ textAlign: 'center' }}>
                                {fetchReviewsError ? t('REVIEW_DESKTOP_ALERT_REVIEW_ERROR') : null}
                            </Card.Body>
                        </Card>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Pager
                        label={t('RESPONDING_REVIEWS_COUNT', { current: currentPage + 1, count: currentPageCount })}
                        canNext={canNextPage!}
                        canPrevious={canPreviousPage!}
                        onNext={nextHandler}
                        onPrevious={previousHandler}
                        disabled={!currentReview}
                    />
                    <Button variant="primary" onClick={finish}>
                        {t('FINISH')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showPopup} centered onHide={() => setShowPopup(false)} backdrop="static">
                <Modal.Header closeButton />
                <Modal.Body>{popupContent}</Modal.Body>
            </Modal>
        </>
    );
};
